import { AbstractPaymentProvidersEvaluatorStrategy } from './AbstractPaymentProvidersEvaluatorStrategy';
import { CryptoPayoutDetails, PayoutTypes } from './PayoutDetails';
import { createPaymentProvider } from './PaymentProvider';
import { PaymentsConfig, TPaymentsConfig } from './transferPaymentsConfig';
import { onrampPaymentMethodIdMap } from './PaymentProviderIdTypes';
import { OrgSettings } from 'features/organization/orgSettings/OrgSettings';

export class CryptoPaymentProvidersEvaluatorStrategy extends AbstractPaymentProvidersEvaluatorStrategy {
  private _cryptoPayouts: CryptoPayoutDetails[];

  constructor(
    paymentProvidersConfig: PaymentsConfig,
    cryptoPayouts: CryptoPayoutDetails[],
    platformIntegrations: Record<string, any>,
    organizationSettings: OrgSettings
  ) {
    super(paymentProvidersConfig, platformIntegrations, organizationSettings);

    this._cryptoPayouts = cryptoPayouts;
  }

  isBatchPayout() {
    return this._cryptoPayouts.length > 1;
  }

  getPayouts() {
    return this._cryptoPayouts;
  }

  private _getFilteredConfiguredProviders() {
    const configuredProvidersAll: TPaymentsConfig['providers'] =
      this.paymentProvidersConfig.get('providers', []);

    const preferredOnrampMethod =
      this.organizationSettings.getPreferredOnrampMethod();
    const configuredOnrampProviders = configuredProvidersAll
      .filter((provider) => provider.isOnrampMethod)
      .filter(({ id }) => {
        return id === onrampPaymentMethodIdMap[preferredOnrampMethod];
      });

    const configuredProvidersRest = configuredProvidersAll.filter(
      (provider) => !provider.isOnrampMethod
    );

    return [...configuredProvidersRest, ...configuredOnrampProviders].filter(
      (provider) => {
        return this.getPayouts().every(
          (p) =>
            !p.inferOperationType() ||
            p.inferOperationType() === provider.operationType
        );
      }
    );
  }

  getAllPossiblePaymentProviders() {
    if (this.isPayoutEligible()) {
      const configuredProviders = this._getFilteredConfiguredProviders();

      return configuredProviders
        .filter((providerConfig) => {
          const isSuitableForPayouts =
            providerConfig.enabled &&
            providerConfig.suitableForPayouts?.includes(
              PayoutTypes.CryptoTransfer
            ) &&
            this._cryptoPayouts.every(
              // @ts-ignore
              (p) => providerConfig.suitableForCurrencies?.includes(p.currency)
            ) &&
            this._cryptoPayouts.every(
              (p) =>
                // @ts-ignore
                providerConfig.suitableForCryptoNetworks?.includes(p.network)
            );

          if (this.isBatchPayout()) {
            return isSuitableForPayouts && providerConfig.batchAvailable;
          }

          return isSuitableForPayouts;
        })
        .map((providerConfig) => createPaymentProvider(providerConfig));
    }

    return [];
  }

  isPayoutEligible() {
    if (
      this._cryptoPayouts.length &&
      this._cryptoPayouts.every(
        (payout) =>
          payout.network === this._cryptoPayouts[0].network &&
          payout.currency === this._cryptoPayouts[0].currency
      )
    ) {
      return true;
    }

    return false;
  }
}
